import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

const testimonials = [
  {
    name: 'Rajesh Kumar',
    location: 'Arkalgud',
    rating: 5,
    text: 'Sagar Ventures transformed our old house into a beautiful modern home. The painting work was exceptional, and the team was very professional. Highly recommended!',
    service: 'Complete Home Renovation',
    avatar: 'RK',
  },
  {
    name: 'Priya Sharma',
    location: 'Arkalgud',
    rating: 5,
    text: 'Their electrical work is top-notch! They installed all new wiring and smart controls in our home. Very clean work and reasonable pricing.',
    service: 'Electrical Services',
    avatar: 'PS',
  },
  {
    name: 'Mohammed Hassan',
    location: 'Arkalgud',
    rating: 5,
    text: 'I called them for plumbing issues and they responded within hours. Fixed everything perfectly and even gave maintenance tips. Great service!',
    service: 'Plumbing Services',
    avatar: 'MH',
  },
  {
    name: 'Lakshmi Devi',
    location: 'Arkalgud',
    rating: 5,
    text: 'From construction to final painting, Sagar Ventures handled everything. Zero stress for us! The team is trustworthy and delivers quality work.',
    service: 'Complete Construction',
    avatar: 'LD',
  },
  {
    name: 'Suresh Gowda',
    location: 'Arkalgud',
    rating: 5,
    text: 'Best construction company in Arkalgud! They completed our bathroom renovation ahead of schedule. The waterproofing work is excellent.',
    service: 'Bathroom Renovation',
    avatar: 'SG',
  },
];

export const TestimonialsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const visibleTestimonials = 3;
  const maxIndex = testimonials.length - visibleTestimonials;

  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, maxIndex]);

  const goToPrev = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  const goToNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  return (
    <section id="testimonials" className="py-20 lg:py-28 bg-background relative overflow-hidden" ref={ref}>
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-20 text-[200px] text-primary">
          <Quote />
        </div>
        <div className="absolute bottom-20 right-20 text-[200px] text-primary rotate-180">
          <Quote />
        </div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="text-primary font-medium uppercase tracking-wider text-sm">
            Testimonials
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mt-4 mb-6">
            What Our <span className="text-gradient-gold">Clients Say</span>
          </h2>
          <p className="text-muted-foreground text-lg">
            Don't just take our word for it — hear from homeowners who trusted us 
            with their dream projects.
          </p>
        </motion.div>

        {/* Testimonials Carousel */}
        <div className="relative px-4 md:px-0">
          {/* Navigation Buttons */}
          <div className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 -translate-x-full">
            <motion.button
              onClick={goToPrev}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="w-12 h-12 rounded-full bg-card shadow-card flex items-center justify-center text-foreground hover:text-primary transition-colors"
            >
              <ChevronLeft className="w-6 h-6" />
            </motion.button>
          </div>
          <div className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 translate-x-full">
            <motion.button
              onClick={goToNext}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="w-12 h-12 rounded-full bg-card shadow-card flex items-center justify-center text-foreground hover:text-primary transition-colors"
            >
              <ChevronRight className="w-6 h-6" />
            </motion.button>
          </div>

          {/* Cards Container */}
          <div className="overflow-hidden">
            <motion.div
              className="flex gap-6"
              animate={{ x: `-${currentIndex * (100 / visibleTestimonials + 2)}%` }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            >
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="flex-shrink-0 w-full md:w-[calc(33.333%-16px)]"
                >
                  <Card className="h-full card-premium border-border/50">
                    <CardContent className="p-6 flex flex-col h-full">
                      {/* Rating */}
                      <div className="flex gap-1 mb-4">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`w-5 h-5 ${
                              i < testimonial.rating
                                ? 'text-primary fill-primary'
                                : 'text-muted-foreground'
                            }`}
                          />
                        ))}
                      </div>

                      {/* Quote */}
                      <div className="testimonial-quote flex-grow mb-6">
                        <p className="text-foreground leading-relaxed">
                          {testimonial.text}
                        </p>
                      </div>

                      {/* Service Badge */}
                      <div className="mb-4">
                        <span className="inline-block bg-primary/10 text-primary text-xs font-medium px-3 py-1 rounded-full">
                          {testimonial.service}
                        </span>
                      </div>

                      {/* Author */}
                      <div className="flex items-center gap-3 pt-4 border-t border-border">
                        <div className="w-12 h-12 rounded-full bg-gradient-gold flex items-center justify-center text-primary-foreground font-semibold">
                          {testimonial.avatar}
                        </div>
                        <div>
                          <h4 className="font-semibold text-foreground">
                            {testimonial.name}
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            {testimonial.location}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Pagination Dots */}
          <div className="flex justify-center gap-2 mt-8">
            {[...Array(maxIndex + 1)].map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setIsAutoPlaying(false);
                  setCurrentIndex(index);
                }}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex
                    ? 'w-8 bg-primary'
                    : 'bg-primary/30 hover:bg-primary/50'
                }`}
              />
            ))}
          </div>

          {/* Mobile Navigation */}
          <div className="flex md:hidden justify-center gap-4 mt-6">
            <motion.button
              onClick={goToPrev}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="w-12 h-12 rounded-full bg-card shadow-card flex items-center justify-center text-foreground"
            >
              <ChevronLeft className="w-6 h-6" />
            </motion.button>
            <motion.button
              onClick={goToNext}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="w-12 h-12 rounded-full bg-card shadow-card flex items-center justify-center text-foreground"
            >
              <ChevronRight className="w-6 h-6" />
            </motion.button>
          </div>
        </div>
      </div>
    </section>
  );
};
